import React from "react";

// The candidate's signed employment contract, completed during the
// Recruitment phase (the accepted Offer). Read-only in onboarding — no
// candidate action is required, and the card always renders as signed.
export interface SignedContract {
  id: number;
  signed: boolean;
  candidateName: string;
  positionTitle: string;
  companyName: string;
  salaryOffered: string | number | null;
  startDate: string | null;
  candidateSignedDate: string | null;
  acceptedAt: string | null;
  content: string | null;
  candidateSignature: string | null;
}

// Date-only strings parse as local midnight so en-AU rendering never shifts
// the day (mirrors the recruitment Offer view).
export const formatContractDate = (dateStr: string | null): string => {
  if (!dateStr) return "—";
  const d = dateStr.includes("T") ? new Date(dateStr) : new Date(`${dateStr}T00:00:00`);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
};

// Rendered document shared by the View dialog/sheet and the PDF download —
// same layout as the recruitment Offer view, so Finance sees the letter,
// the key terms and the candidate's captured signature. `bare` strips the
// card chrome and uses print-oriented letter classes for the html2canvas
// capture (mirrors the recruitment Offer letter PDF).
export const SignedContractDocument: React.FC<{ contract: SignedContract; bare?: boolean }> = ({
  contract,
  bare = false,
}) => (
  <div
    className={
      bare
        ? "bg-white border border-gray-200 p-12 pb-20"
        : "bg-white rounded-xl shadow-sm border border-gray-200 p-8 sm:p-12 pb-16 sm:pb-20"
    }
  >
    <div className="text-center mb-8">
      <h1 className="text-2xl font-bold text-gray-900">Offer of Employment</h1>
      {contract.companyName && (
        <p className="text-gray-500 text-sm mt-1">{contract.companyName}</p>
      )}
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 bg-slate-50 rounded-lg p-4 border border-slate-100">
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Candidate</p>
        <p className="text-sm font-medium text-gray-900 mt-0.5">{contract.candidateName || "—"}</p>
      </div>
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Position</p>
        <p className="text-sm font-medium text-gray-900 mt-0.5">{contract.positionTitle || "—"}</p>
      </div>
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Salary</p>
        <p className="text-sm font-medium text-gray-900 mt-0.5">
          {contract.salaryOffered === null || contract.salaryOffered === undefined || contract.salaryOffered === ""
            ? "—"
            : isNaN(Number(contract.salaryOffered))
            ? String(contract.salaryOffered)
            : `$${Number(contract.salaryOffered).toLocaleString()}`}
        </p>
      </div>
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Start Date</p>
        <p className="text-sm font-medium text-gray-900 mt-0.5">
          {formatContractDate(contract.startDate)}
        </p>
      </div>
    </div>

    {contract.content ? (
      <div
        className={
          bare
            ? "text-slate-800 text-[15px] leading-relaxed contract-letter-pdf"
            : "prose prose-sm max-w-none text-slate-800 text-[15px] leading-relaxed"
        }
        dangerouslySetInnerHTML={{ __html: contract.content }}
      />
    ) : (
      <p className="text-slate-800 text-[15px] leading-relaxed">No contract content available.</p>
    )}

    {contract.candidateSignature && (
      <div className="mt-8 pt-6 border-t border-gray-200">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
          Digital Signature
        </p>
        <img src={contract.candidateSignature} alt="Candidate signature" className="h-16 object-contain" />
        <p className="text-xs text-slate-400 mt-1">
          Signed on {formatContractDate(contract.candidateSignedDate)}
        </p>
      </div>
    )}
  </div>
);
